import { createAvroEventsHub } from "@zapier/avro-events";
import type {
  EventArgs,
  EventFieldsSetByAPI,
  EventFieldsSetByEventsToolkit,
  EventNameType,
  WebAnalyticsTrackingUserInteractionUserInteractionEvent,
} from "@zapier/avro-events";

import { emit } from "@zapier/events-toolkit-browser";
import {
  UserInteractionEvent,
  UserInteractionEventName,
} from "./trackingTypes";
import { FullStoryAPI } from "@zapier/react-fullstory";

const SYSTEM_NAME = "templates";

type RemoveAutoPopulatedEventFields<Type> = {
  [Property in keyof Type as Exclude<
    Property,
    EventFieldsSetByAPI | EventFieldsSetByEventsToolkit
  >]: Type[Property];
};

const emitEvent = <T extends EventNameType>(
  eventName: T | UserInteractionEventName,
  customEventData?: RemoveAutoPopulatedEventFields<EventArgs<T>>,
  systemName?: string,
) => {
  return createAvroEventEmitter(eventName as EventNameType)({
    system_name: systemName || SYSTEM_NAME,
    ...(customEventData || {}),
  });
};

export default emitEvent;

export const emitUserInteractionEvent = (
  name: UserInteractionEventName,
  properties: Omit<
    RemoveAutoPopulatedEventFields<WebAnalyticsTrackingUserInteractionUserInteractionEvent.EventFields>,
    "system_name"
  >,
) => {
  const payload = {
    ...properties,
    interaction_name: name,
  };
  return createAvroEventEmitter(UserInteractionEvent)(payload);
};

export const emitZapEntryPointEvent = createAvroEventEmitter(
  "event.zapier.zap.creation.ZapEntryPointEvent",
);

export const emitZapCreationSuccessEvent = createAvroEventEmitter(
  "zap.creation.ZapCreationRequestSuccessEvent",
);

export type AvroEventEmitter<T extends EventNameType> = (
  data: Omit<RemoveAutoPopulatedEventFields<EventArgs<T>>, "system_name">,
) => Promise<void>;

function createAvroEventEmitter<T extends EventNameType>(
  eventName: T,
): AvroEventEmitter<T> {
  const event = createAvroEventsHub<T>();
  event.emitFunction = emit;

  return async function emitEvent(data) {
    // We can't seem to convince TypeScript that `Omit`ing "system_name" then
    // adding it back in is a safe operation. It obviously is, though, so I'm
    // forcing this code with an `as any` to make it work.
    const fullData: RemoveAutoPopulatedEventFields<EventArgs<T>> = {
      system_name: SYSTEM_NAME,
      ...data,
    } as any;

    // FS events are disabled in dev mode by default
    FullStoryAPI(
      "event",
      // @ts-expect-error // interaction_name might not exist on data
      fullData.interaction_name || eventName,
      fullData as { [property: string]: any },
    );

    event.emit(fullData, eventName);
  };
}
